import { useEffect } from "react";
import { Link } from "react-router-dom";
import type { AdminCustomer } from "@/mocks/admin";
import { allAdminBookings } from "@/mocks/admin";

type Props = {
  customer: AdminCustomer | null;
  onClose: () => void;
};

const statusPill: Record<AdminCustomer["status"], string> = {
  active: "bg-primary-100 text-primary-700",
  new: "bg-accent-100 text-accent-700",
  churned: "bg-background-200 text-foreground-700",
};

const statusLabel: Record<AdminCustomer["status"], string> = {
  active: "Active",
  new: "New",
  churned: "Churned",
};

const paymentTint: Record<string, string> = {
  paid: "bg-primary-100 text-primary-700",
  due: "bg-accent-100 text-accent-700",
  refunded: "bg-background-200 text-foreground-700",
};

export default function CustomerDrawer({ customer, onClose }: Props) {
  useEffect(() => {
    if (!customer) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [customer, onClose]);

  if (!customer) return null;

  const history = allAdminBookings
    .filter((b) => b.customer === customer.name)
    .slice(0, 5);

  return (
    <div className="fixed inset-0 z-50">
      <div
        className="absolute inset-0 bg-foreground-950/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      ></div>
      <aside className="absolute right-0 top-0 h-full w-full md:w-[440px] bg-background-50 overflow-y-auto animate-slide-in">
        <div className="flex items-center justify-between px-5 pt-5">
          <p className="text-xs uppercase tracking-widest text-foreground-500">
            Customer profile
          </p>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full text-foreground-600 hover:bg-background-100 cursor-pointer"
            aria-label="Close"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="px-5 pb-6 pt-3">
          {/* Identity */}
          <div className="flex items-center gap-4">
            <img
              src={customer.avatar}
              alt={customer.name}
              className="w-16 h-16 rounded-full object-cover object-top"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-heading text-xl text-foreground-950 truncate">
                  {customer.name}
                </h3>
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${statusPill[customer.status]}`}
                >
                  {statusLabel[customer.status]}
                </span>
              </div>
              <p className="text-xs text-foreground-600 mt-0.5 truncate">
                {customer.email}
              </p>
              <p className="text-xs text-foreground-600 truncate">
                {customer.city} · {customer.plan}
              </p>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-2 mt-5">
            <div className="bg-background-100 rounded-lg px-3 py-2.5 text-center">
              <p className="font-heading text-lg text-foreground-950">
                {customer.bookings}
              </p>
              <p className="text-[10px] text-foreground-600 uppercase tracking-wider">
                Bookings
              </p>
            </div>
            <div className="bg-background-100 rounded-lg px-3 py-2.5 text-center">
              <p className="font-heading text-lg text-foreground-950">
                ${customer.lifetime.toLocaleString()}
              </p>
              <p className="text-[10px] text-foreground-600 uppercase tracking-wider">
                Lifetime
              </p>
            </div>
            <div className="bg-background-100 rounded-lg px-3 py-2.5 text-center">
              <p className="font-heading text-sm text-foreground-950 truncate">
                {customer.lastBooking}
              </p>
              <p className="text-[10px] text-foreground-600 uppercase tracking-wider">
                Last
              </p>
            </div>
          </div>

          {/* Quick actions */}
          <div className="flex items-center gap-2 mt-5">
            <a
              href={`mailto:${customer.email}`}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-md bg-background-100 hover:bg-background-200 text-foreground-900 text-xs font-medium whitespace-nowrap"
            >
              <i className="ri-mail-line"></i>
              Email
            </a>
            <Link
              to="/admin/bookings"
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-md bg-background-100 hover:bg-background-200 text-foreground-900 text-xs font-medium whitespace-nowrap"
            >
              <i className="ri-calendar-line"></i>
              Bookings
            </Link>
            <Link
              to="/booking"
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-xs font-medium whitespace-nowrap"
            >
              <i className="ri-add-line"></i>
              New
            </Link>
          </div>

          {/* Booking history */}
          <div className="mt-6">
            <p className="text-[10px] uppercase tracking-widest text-foreground-500 mb-2">
              Recent bookings
            </p>
            {history.length === 0 ? (
              <div className="bg-background-100 rounded-lg px-4 py-5 text-center">
                <p className="text-xs text-foreground-600">No booking history yet</p>
              </div>
            ) : (
              <ul className="bg-background-100 rounded-lg divide-y divide-background-200 overflow-hidden">
                {history.map((b) => (
                  <li key={b.id} className="flex items-center gap-3 px-3.5 py-2.5">
                    <span className="w-7 h-7 flex items-center justify-center rounded-md bg-background-50 text-foreground-700">
                      <i className={`${b.serviceIcon} text-sm`}></i>
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-foreground-950 truncate">
                        {b.service}
                      </p>
                      <p className="text-[10px] text-foreground-600 truncate">
                        {b.dateLabel} · {b.time} · {b.cleanerName || "Unassigned"}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-0.5">
                      <span className="text-xs font-semibold text-foreground-950">
                        ${b.amount}
                      </span>
                      <span
                        className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-medium ${paymentTint[b.payment]}`}
                      >
                        {b.payment}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Tags / preferences */}
          <div className="mt-6">
            <p className="text-[10px] uppercase tracking-widest text-foreground-500 mb-2">
              Plan
            </p>
            <div className="bg-background-100 rounded-lg px-4 py-3 flex items-center gap-3">
              <span className="w-9 h-9 flex items-center justify-center rounded-md bg-background-50 text-foreground-700">
                <i className="ri-repeat-line text-base"></i>
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-foreground-950">
                  {customer.plan}
                </p>
                <p className="text-[10px] text-foreground-600">
                  {customer.plan === "One-time"
                    ? "No recurring schedule"
                    : "Recurring schedule active"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}